import { Float } from '@headlessui-float/react';
import { Popover, PopoverButton } from '@headlessui/react';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@remix-run/react';
import { GQLProductTileFragment } from '@theprintbar/graphql';
import { cn } from '@theprintbar/shared';
import clsx from 'clsx';
import { useState } from 'react';
import { ProductQueryKeys, ProductSearchQuery, ProductSortOptions, getProductSearchURL } from './productSearch';

interface SortByProps {
	className?: string;
	products: GQLProductTileFragment[];
	setProducts: React.Dispatch<any>;
	params: ProductSearchQuery;
}

export function SortBy({ className, products, setProducts, params }: SortByProps) {
	const [currentSort, setCurrentSort] = useState<any>(null);
	const navigate = useNavigate();

	const getUrl = (sortKey: ProductSortOptions) => getProductSearchURL(ProductQueryKeys.Sort, sortKey, params);

	function handleSort(direction: 'asc' | 'desc') {
		const sortedProducts = [...products].sort((a, b) => {
			const aPrice = parseInt(a.salePrice, 10);
			const bPrice = parseInt(b.salePrice, 10);
			if (direction === 'asc') {
				setCurrentSort('priceAsc');
				return aPrice - bPrice;
			} else {
				setCurrentSort('priceDesc');
				return bPrice - aPrice;
			}
		});
		setProducts(sortedProducts);
	}

	const SortOption = ({ sortKey, active, children }: { sortKey: ProductSortOptions; active: Boolean; children: React.ReactNode }) => (
		<button
			type="button"
			onClick={() => {
				setCurrentSort(sortKey);
				navigate(getUrl(sortKey));
			}}
			className={cn('py-2 px-4 text-left', active ? 'bg-blue/10' : 'hover:bg-blue/5')}
		>
			{children}
		</button>
	);

	return (
		<Popover>
			{({ open }) => (
				<Float
					placement="bottom-end"
					offset={13}
					arrow={5}
					transform={false}
					floatingAs="div"
					enter="transition ease-out duration-100"
					enterFrom="transform opacity-0 scale-95"
					enterTo="transform opacity-100 scale-100"
					leave="transition ease-in duration-75"
					leaveFrom="transform opacity-100 scale-100"
					leaveTo="transform opacity-0 scale-95"
					tailwindcssOriginClass
				>
					<PopoverButton className={clsx('font-sans text-sm lg:text-base outline-none space-x-2 items-center flex', className)}>
						<span>Sort By</span> {open ? <ArrowUpIcon className="h-4 w-4" /> : <ArrowDownIcon className="h-4 w-4" />}
					</PopoverButton>

					<div className="flex flex-col w-44 rounded-md shadow-lg border border-blue bg-white-pearl">
						<SortOption
							active={!params.sort || params.sort === ProductSortOptions.Popular}
							sortKey={ProductSortOptions.Popular}
						>
							Most Popular
						</SortOption>
						<SortOption active={params.sort === ProductSortOptions.Latest} sortKey={ProductSortOptions.Latest}>
							Latest
						</SortOption>
						<SortOption active={params.sort === ProductSortOptions.PriceAsc} sortKey={ProductSortOptions.PriceAsc}>
							Price Ascending
						</SortOption>
						<SortOption active={params.sort === ProductSortOptions.PriceDesc} sortKey={ProductSortOptions.PriceDesc}>
							Price Descending
						</SortOption>
					</div>
				</Float>
			)}
		</Popover>
	);
}
